import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';

/**
 * Detects if the page was reloaded.
 * Uses the Performance API to check navigation type.
 *
 * @returns true if the page was reloaded, false otherwise
 */
const isPageReload = (): boolean => {
    const navigation = performance.getEntriesByType('navigation')[0] as
        PerformanceNavigationTiming | undefined;

    return navigation?.type === 'reload';
};

// This module is re-evaluated after a browser refresh.
let shouldForceTokenRefresh = isPageReload();

/**
 * Gets the currently selected organization ID.
 * For single-tenant applications, returns the organization ID from environment variables.
 *
 * @returns The organization ID from VITE_ORGANIZATION_ID or null if not set
 */
const getSelectedOrgId = (): string | null =>
    import.meta.env.VITE_ORGANIZATION_ID ?? null;

/**
 * Gets an access token with organization context and optional forced refresh.
 * For single-tenant applications, includes organization from environment variables.
 *
 * @param getAccessTokenSilently - Auth0 function to get access token
 * @param options - Optional parameters
 * @param options.forceRefresh - Force token refresh (cacheMode: 'off')
 * @returns Promise resolving to the access token
 * @throws Error if token retrieval fails
 */
export const getTokenWithOrgContext = async (
    getAccessTokenSilently: (
        options?: GetTokenSilentlyOptions,
    ) => Promise<string>,
    options?: { forceRefresh?: boolean },
): Promise<string> => {
    const orgId = getSelectedOrgId();
    const forceRefresh = options?.forceRefresh ?? shouldForceTokenRefresh;

    if (forceRefresh) {
        shouldForceTokenRefresh = false;
    }

    return getAccessTokenSilently({
        ...(forceRefresh && { cacheMode: 'off' }),
        ...(orgId && {
            authorizationParams: {
                organization: orgId,
            },
        }),
    });
};
